import { useEffect, useMemo, useRef, useState } from 'react'
import { useMediaDeviceSelect } from '@livekit/components-react'
import { LuMic, LuSettings, LuVideo } from 'react-icons/lu'
import DeviceSelect from './DeviceSelect.jsx'

/** Device list → react-select options. Deduped by deviceId, with a numbered
 * fallback label because the browser hides real labels until a mic/camera
 * permission has been granted at least once. Same rules as the VideoSDK
 * button so the two popovers list hardware identically. */
function toOptions(devices, fallbackLabel) {
  const seen = new Set()
  const options = []
  ;(devices ?? []).forEach((d) => {
    if (!d?.deviceId || seen.has(d.deviceId)) return
    seen.add(d.deviceId)
    options.push({ value: d.deviceId, label: d.label?.trim() || `${fallbackLabel} ${options.length + 1}` })
  })
  return options
}

/**
 * The LiveKit twin of DeviceSettingsButton — the identical gear button +
 * Microphone/Camera popover, for call UIs mounted inside a <LiveKitRoom>.
 * Takes no props, same as the VideoSDK one, so both stages mount the same
 * control.
 *
 * Switching goes through LiveKit's `room.switchActiveDevice` (via
 * useMediaDeviceSelect): a live track is swapped in place, and a pick made
 * while that device is OFF is simply remembered by the room as the capture
 * default for the next time it turns on — so, unlike the VideoSDK button,
 * no deferred-apply bookkeeping is needed here.
 */
export default function LiveKitDeviceSettingsButton() {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  // requestPermissions stays false: the room has already asked for whatever
  // the user chose to publish, and a fresh getUserMedia here would pop a
  // second permission prompt on some browsers. Labels fill in once any
  // device permission exists, which the join itself normally provides.
  const mic = useMediaDeviceSelect({ kind: 'audioinput', requestPermissions: false })
  const cam = useMediaDeviceSelect({ kind: 'videoinput', requestPermissions: false })

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const pick = (kind, deviceId) => {
    const target = kind === 'mic' ? mic : cam
    Promise.resolve(target.setActiveMediaDevice(deviceId)).catch((err) =>
      console.error(`[LiveKitDeviceSettingsButton] ${kind} switch failed:`, err)
    )
  }

  const micOptions = useMemo(() => toOptions(mic.devices, 'Microphone'), [mic.devices])
  const camOptions = useMemo(() => toOptions(cam.devices, 'Camera'), [cam.devices])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Device settings"
        aria-haspopup="true"
        aria-expanded={open}
        title="Device settings"
        className={`w-12 h-12 rounded-full flex items-center justify-center border transition duration-200 ${
          open
            ? 'bg-white/20 border-white/30 text-white'
            : 'bg-white/10 border-white/20 text-white/90 hover:bg-white/15'
        }`}
      >
        <LuSettings className="w-5 h-5" />
      </button>
      {open && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-50 w-72 max-w-[calc(100vw-2rem)] rounded-xl bg-[#171310] border border-white/15 shadow-2xl p-4 space-y-4 text-left">
          <p className="text-[11px] font-bold uppercase tracking-wider text-white/70">Device Settings</p>
          <DeviceSelect
            icon={LuMic}
            label="Microphone"
            inputId="livekit-device-settings-mic"
            options={micOptions}
            value={mic.activeDeviceId || null}
            onChange={(id) => pick('mic', id)}
          />
          <DeviceSelect
            icon={LuVideo}
            label="Camera"
            inputId="livekit-device-settings-cam"
            options={camOptions}
            value={cam.activeDeviceId || null}
            onChange={(id) => pick('cam', id)}
          />
        </div>
      )}
    </div>
  )
}
